import { Button, Col, message, Pagination, Popover, Row, Space, Spin, Table } from 'antd';
import React, { useState } from 'react';
import {
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    NotificationOutlined,
    PauseCircleOutlined,
    PlusCircleOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import { Campaign, CampaignNotificationResult } from '../../../shared/types';
import { useSortedCampaignQuery } from '../hooks/useSortedCampaignQuery';
import PopConfirm from '../../../shared/Components/PopConfirm';
import { useMutation } from '@apollo/client';
import {
    CITRIN_DELETE_CAMPAIGN,
    CITRIN_SEND_PUSHNOTIFICATION_CAMPAIGN,
    DeleteCampaignRequest,
    DeleteCampaignResponse,
    PushNotificationCampaignRequest,
    PushNotificationCampaignResponse,
} from '../../../GraphQL';
import CampaignCreateModal from './CampaignCreateModal';
import { useCurrentCampaign } from '../hooks/useCurrentCampaign';
import CampaignEditModal from './CampaignEditModal';
import { convertDateToFormat } from '../../../shared/Utils';

const CampaignTable = () => {
    const [isCreateModalVisible, setCreateModalVisible] = useState(false);
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const { sortedCampaigns: campaigns, refetch, loading, ...pagination } = useSortedCampaignQuery();

    const [deleteMutation] = useMutation<DeleteCampaignResponse, DeleteCampaignRequest>(CITRIN_DELETE_CAMPAIGN);
    const [pushNotificationCampaignMutation] = useMutation<
        PushNotificationCampaignResponse,
        PushNotificationCampaignRequest
    >(CITRIN_SEND_PUSHNOTIFICATION_CAMPAIGN);
    const { setCurrentCampaign } = useCurrentCampaign();

    const [isLoading, setIsLoading] = useState(false);
    const [campaignResult, setCampaignResult] = useState<Record<string, CampaignNotificationResult>>({});

    const handleRemoveCampaign = (campaign: Campaign) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: campaign._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSetPushNotificationCampaign = async (campaign: Campaign) => {
        setIsLoading(true);
        try {
            const response = await pushNotificationCampaignMutation({
                variables: { id: campaign._id },
            });

            const result = response.data?.CITRIN_MGT_sendPushNotificationCampaign;

            setCampaignResult((prev) => ({
                ...prev,
                [campaign._id]: {
                    ...result,
                    campaignId: campaign._id,
                },
            }));

            if (
                result.message.includes('inactive') ||
                result.message.includes('not started') ||
                result.message.includes('ended')
            ) {
                message.error(result.message);
            } else {
                message.success(result.message);
                refetch();
            }
        } catch (err) {
            console.error(err);
            message.error('An error occurred while sending the notification.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateCampaign = () => {
        setCreateModalVisible(true);
    };

    const handleEditCampaign = (campaign: Campaign) => {
        setCurrentCampaign(campaign);
        setEditModalVisible(true);
    };
    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Audience',
            dataIndex: 'targetAudience',
            key: 'targetAudience',
        },
        {
            title: 'Starting date',
            dataIndex: 'startingDate',
            width: 130,
            key: 'startingDate',
            sorter: (a, b) => a.startingDate - b.startingDate,
            render: (text, record) => {
                return convertDateToFormat(record.startingDate, 'DD-MM-YYYY');
            },
        },
        {
            title: 'End date',
            dataIndex: 'endDate',
            width: 130,
            key: 'endDate',
            sorter: (a, b) => a.endDate - b.endDate,
            render: (text, record) => {
                return convertDateToFormat(record.endDate, 'DD-MM-YYYY');
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text, record: Campaign) => {
                return record.status ? (
                    <CheckCircleOutlined title="Active" style={{ fontSize: '16px', color: 'green' }} />
                ) : (
                    <PauseCircleOutlined title="Inactive" style={{ fontSize: '16px', color: 'red' }} />
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, campaign) => (
                <Space size="middle">
                    <PopConfirm
                        title={'Delete the campaign'}
                        description={'Are you sure to delete this campaign?'}
                        onConfirmTask={() => handleRemoveCampaign(campaign)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={<DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />}
                    />
                    <a href="#" onClick={() => handleEditCampaign(campaign)}>
                        <EditOutlined style={{ fontSize: '16px', color: 'green' }} />
                    </a>

                    <Popover
                        title="Campaign results"
                        trigger="hover"
                        content={
                            campaignResult?.campaignId !== campaign._id && campaignResult[campaign._id] ? (
                                <div>
                                    <p>Total: {campaignResult[campaign._id]?.totalRecipients}</p>
                                    <p style={{ color: 'green' }}>
                                        Success: {campaignResult[campaign._id]?.successCount}
                                    </p>
                                    <p style={{ color: 'red' }}>Failed: {campaignResult[campaign._id]?.failedCount}</p>
                                    {campaignResult[campaign._id]?.failedRecipients?.length > 0 && (
                                        <div>
                                            <b>Failed targets users:</b>
                                            <ul style={{ paddingLeft: 15, marginTop: 5 }}>
                                                {campaignResult[campaign._id]?.failedRecipients.map((f, index) => (
                                                    <li key={index}>
                                                        ({f.userType.toLocaleLowerCase() + '_id: ' + f.userId})
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span>Campaign not run yet</span>
                            )
                        }
                    >
                        <Button
                            icon={
                                <NotificationOutlined title="Brodcast" style={{ fontSize: '16px', color: 'green' }} />
                            }
                            size={'middle'}
                            onClick={() => handleSetPushNotificationCampaign(campaign)}
                        />
                    </Popover>
                </Space>
            ),
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Campaigns ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            <CampaignCreateModal
                updateList={refetch}
                isOpen={isCreateModalVisible}
                onRequestClose={setCreateModalVisible}
            />

            <Row justify={'end'}>
                <Button
                    type="primary"
                    shape="round"
                    icon={<PlusCircleOutlined />}
                    size={'middle'}
                    onClick={handleCreateCampaign}
                >
                    Add
                </Button>
            </Row>

            {isEditModalVisible && (
                <CampaignEditModal
                    updateList={refetch}
                    isOpen={isEditModalVisible}
                    onRequestClose={setEditModalVisible}
                />
            )}

            <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                <Col xs={24}>
                    <Spin spinning={isLoading || loading}>
                        <Table
                            dataSource={campaigns}
                            columns={columns}
                            pagination={false}
                            rowKey={() => Math.random().toString()}
                            scroll={{ x: 1500 }}
                        ></Table>
                    </Spin>
                </Col>
            </Row>
            <Paginator />
        </>
    );
};

export default CampaignTable;
